import React, {useState} from 'react';
import type {ReactElement} from 'react';
import {Notification} from '@Components/notification';
import {isUserPremium} from '@Libraries/user.library';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {formatNumber, formatToNearestThousand} from '@Utils/number.util';
import styles from '@Components/poster-editor/components/remove-video-background-notification-tag/remove-video-background-notification-tag.module.scss';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import {Text, TextSize, TextType} from '@Components/text';
import {useGetRemoveVideoBackgroundCreditsCost} from '@Hooks/useGetRemoveVideoBackgroundCreditsCost';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {useAppSelector} from '@/hooks';

interface RemoveVideoBackgroundNotificationTagProps {
  videoHashedFilename: string;
  startTime: number;
  endTime: number;
}

export function RemoveVideoBackgroundNotificationTag({...props}: RemoveVideoBackgroundNotificationTagProps): ReactElement<any> | null {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const backgroundRemovalCreditsCost = useGetRemoveVideoBackgroundCreditsCost(props.videoHashedFilename, props.startTime, props.endTime);
  const credits = useGetTeamCredits() ?? 0;
  const areCreditsSufficient = typeof backgroundRemovalCreditsCost === 'number' && credits >= backgroundRemovalCreditsCost;
  const isPremiumUser = isUserPremium();
  const videoBGRPromo = useAppSelector((state) => state.teamCredits.videoBGRPromo);

  if (backgroundRemovalCreditsCost === 0) {
    if (videoBGRPromo?.isEligible) {
      return (
        <div className={styles.container}>
          <Notification isRound={false} className={`${styles.notification} ${styles.enabledNotification}`} text={window.i18next.t('pmwjs_free')} />
        </div>
      );
    }
    return null;
  }

  if (areCreditsSufficient) {
    return null;
  }

  const getRoundedBgCredits = (): string => {
    if (credits === null) {
      return '...';
    }

    return formatToNearestThousand(credits);
  };

  const getFormattedCredits = (): string => {
    if (credits === null) {
      return '...';
    }

    return formatNumber(credits);
  };

  if (!isPremiumUser) {
    return (
      <div className={styles.container}>
        <Icon icon="icon-crown" size={IconSize.SIZE_ICON_20} className="color-premium" />
      </div>
    );
  }

  const getNotificationBackgroundStyle = (): string => {
    if (areCreditsSufficient) {
      if (showTooltip) {
        return styles.enabledHoveredNotification;
      }
      return styles.enabledNotification;
    }

    if (showTooltip) {
      return styles.disbledHoveredNotification;
    }
    return styles.disabledNotification;
  };

  const getTooltipContent = (): ReactElement<any> => {
    const costStr =
      backgroundRemovalCreditsCost !== null && backgroundRemovalCreditsCost > 1
        ? window.i18next.t('pmwjs_will_cost_x_credit', {creditsCost: backgroundRemovalCreditsCost})
        : window.i18next.t('pmwjs_will_cost_1_credit');
    return (
      <div className="flex-center flex-v-row">
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: getFormattedCredits()})} />
        <Text dangerouslySetInnerHTML type={TextType.BODY} size={TextSize.XSMALL} val={costStr} />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <Notification
        isRound={false}
        className={`${styles.notification} hover-transition-all ${getNotificationBackgroundStyle()}`}
        showLoading={credits === null}
        text={getRoundedBgCredits()}
        onMouseEnter={(): void => {
          if (!showTooltip) {
            setShowTooltip(true);
          }
        }}
        onMouseLeave={(): void => {
          if (showTooltip) {
            setShowTooltip(false);
          }
        }}
      />
      {credits !== null && showTooltip ? (
        <Tooltip className={styles.tooltip} position={TOOLTIP_POSITION.BOTTOM_RIGHT} id="outline-remove-video-bg-credits-tooltip" text={getTooltipContent()} />
      ) : null}
    </div>
  );
}
